/* ===========================================================================
   AEVO — Shopify Inbox chat widget positioning

   Two behaviours:
     1. Hide the chat launcher while the cart drawer is open.
     2. Sit close to the bottom of the screen normally, and lift above the
        sticky add to cart bar whenever that bar is showing.

   The widget is <shopify-chat>, a custom element with an open shadow root.
   Two things it exposes make this safe to drive from outside:

     --shopify-agent-activator-offset  the app's own knob for how far the
                                       launcher sits off the bottom (20px
                                       by default)
     part="activator"                  on the launcher button, so it can be
                                       targeted with ::part() from custom.css

   IMPORTANT — do not style the <shopify-chat> host's box or hit-testing.
   The host is a full-viewport fixed container holding both the launcher and
   the chat panel. Setting pointer-events on it turns the whole page into an
   invisible click trap. This file only ever writes that one custom property,
   plus an attribute that custom.css keys off. Nothing else.

   The sticky bar's shown/hidden state has no stable class to hook — the
   vendor's minified script drives it — so its position is measured instead.
   =========================================================================== */
(function () {
  'use strict';

  /* Tweak these two numbers to taste. */
  var GAP_BOTTOM = 12; /* px above the bottom of the screen, no sticky bar */
  var GAP_ABOVE_STICKY = 12; /* px of clearance above the sticky bar */

  var chatEl = null;
  var lastOffset = null;
  var lastHidden = null;

  function cartIsOpen() {
    var drawer = document.querySelector('cart-drawer');
    return !!drawer && drawer.classList.contains('active');
  }

  /* How many px of the sticky bar are showing above the bottom edge, or 0
     when it isn't there. Mid-transition this returns a partial height, so
     the launcher tracks the bar rather than jumping. */
  function stickyBarExposedHeight() {
    var bar = document.querySelector('.sticky-atc');
    if (!bar) return 0;

    var style = window.getComputedStyle(bar);
    if (style.display === 'none' || style.visibility === 'hidden') return 0;
    if (parseFloat(style.opacity) === 0) return 0;

    var rect = bar.getBoundingClientRect();
    if (!rect.height) return 0;

    var exposed = window.innerHeight - rect.top;
    if (exposed < 1) return 0; /* translated fully off the bottom */
    return Math.min(exposed, rect.height);
  }

  function apply() {
    if (!chatEl) return;

    var exposed = stickyBarExposedHeight();
    var offset = exposed > 0 ? Math.round(exposed) + GAP_ABOVE_STICKY : GAP_BOTTOM;
    var hidden = cartIsOpen();

    /* Write only on change. Unconditional writes would be seen by the
       observer below as fresh mutations and reschedule this forever. */
    if (offset !== lastOffset) {
      lastOffset = offset;
      chatEl.style.setProperty('--shopify-agent-activator-offset', offset + 'px');
    }

    if (hidden !== lastHidden) {
      lastHidden = hidden;
      if (hidden) {
        chatEl.setAttribute('data-aevo-hide', '');
      } else {
        /* Removed rather than set to a "shown" value — the app decides what
           shown looks like, this only ever adds a reason to hide. */
        chatEl.removeAttribute('data-aevo-hide');
      }
    }
  }

  var queued = false;
  function schedule() {
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(function () {
      queued = false;
      apply();
    });
  }

  function watch() {
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('transitionend', schedule, true);

    /* Catches the cart drawer opening/closing and the sticky bar's attribute
       flips, neither of which fire an event we can listen for. Scoped to the
       attributes that matter — watching 'style' would pick up this file's
       own writes and loop. */
    new MutationObserver(schedule).observe(document.body, {
      attributes: true,
      attributeFilter: ['class', 'data-after-scroll', 'data-display-mode'],
      subtree: true
    });
  }

  function init() {
    var found = document.querySelector('shopify-chat');
    if (!found) return false;
    chatEl = found;
    watch();
    apply();
    return true;
  }

  if (!init()) {
    /* The app injects the widget after page load, so wait for it. */
    var observer = new MutationObserver(function () {
      if (init()) observer.disconnect();
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    /* Give up after 30s — the app may simply be off. */
    window.setTimeout(function () {
      observer.disconnect();
    }, 30000);
  }
})();
